import {Component, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {BaseChartComponent, BatteryType, EnergyType, Interval} from './base-chart.component';
import {BaseChartDirective} from 'ng2-charts';
import {AverageEnergyResponse} from '../../../../api/models/average-energy-response';
import {Chart, ChartOptions} from 'chart.js';
import {de} from 'date-fns/locale';
import annotationPlugin from 'chartjs-plugin-annotation';
import {DateTimeValue} from '../../../../api/models/date-time-value';
import {getAverageEnergyStatus, AverageEnergyKey} from './average-energy-response.helper';

@Component({
  selector: 'app-battery-charge-monthly-chart',
  templateUrl: './chart.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [BaseChartDirective],
})
export class BatteryChargeMonthlyChartComponent extends BaseChartComponent implements OnChanges {
  override interval: Interval = 'month'; // Define input with default value
  type: EnergyType = 'battery'; // Define input with default value
  batteryType: BatteryType = 'charge'; // Define input with default value

  @Input() date: Date = new Date();
  @Input() data: AverageEnergyResponse | undefined;

  // Override unit to % for battery charge charts, kW for power charts
  @Input() override get unit(): string {
    return  '%';
  }

  override set unit(value: string) {
    // Allow external setting but ignore it since we determine unit based on batteryType
  }

  monthLineChartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        stacked: false,
        beginAtZero: true,
        border: {
          display: false,
        },
        ticks: {
          callback: function (value) {
            return value + '%';
          },
        },
      },
      x: {
        type: 'time',
        time: {
          unit: 'day',
          tooltipFormat: 'dd MM',
          displayFormats: !this.isMobile()? {day: 'dd MM'} : {day: 'd'},
        },
        adapters: {
          date: {
            locale: de,
          },
        },
        ticks: {
          maxRotation: 90,
          minRotation: 90,
          autoSkip: true,
          callback: (value) => {
            const date = new Date(value);
            return this.isMobile()
              ? date.getDate()
              : date.toLocaleDateString('de-AT', {
                  day: '2-digit',
                  month: 'numeric',
                  timeZone: this.timezone,
                });
          },
        },
        border: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
      },
      ...this.baseTooltipOptions,
    },
    elements: {
      line: {
        // Disable bezier interpolation so segments are straight between points
        tension: 0,
      },
    },
  };

  constructor() {
    super();
    // Register the annotation plugin
    Chart.register(annotationPlugin);
  }

  ngOnChanges(changes: SimpleChanges): void {
    console.log('Performance battery charge monthly chart received change', changes);
    // Reset the chart data when type or batteryType changes
    if (changes['data']) {
      this.onResourceChange();
    }
  }

  private onResourceChange() {
    if (!this.data) {
      return; // Exit if no data is available
    }
    this.initializeChartData();
  }

  private initializeChartData() {
    console.log('initialize Performance Chart');
    if (!this.data) {
      console.log('No data available');
      return; // Exit if no data is available
    }

    const typeName =
      this.type +
      this.batteryType.charAt(0).toUpperCase() +
      this.batteryType.slice(1); // Set the type to batteryType

    const dataItems = [getAverageEnergyStatus(this.data, typeName as AverageEnergyKey)];
    const datasets: any[] = [];
    const allLabels: Date[][] = [];

    let index = 0;
    dataItems.forEach((dataItem) => {
      if (!dataItem || !dataItem.deviceTimeValues) return;

      // Add original deviceTimeValues datasets
      Object.entries(dataItem.deviceTimeValues).forEach(([key, timeValues]) => {
        // Type guard to ensure timeValues is an array of DateTimeValue
        if (!Array.isArray(timeValues) || !this.isThereData(timeValues as DateTimeValue[])) return;
        // Ensure timeValues is complete
        const completedTimeValues: DateTimeValue[] = this.completeData(timeValues as DateTimeValue[], this.interval, this.date,'null', dataItem.timeBucket);
        const rawTimeValues: DateTimeValue[] = completedTimeValues.map((tv) => ({...tv}));

        // Extract labels from this dataset
        const datasetLabels: Date[] = [];
        completedTimeValues.forEach((item) => {
          const date = new Date(item.read ?? '');
          if (!isNaN(date.getTime())) {
            datasetLabels.push(date);
          }
        });
        allLabels.push(datasetLabels);

        // set color and label for dataset
        const color = this.getGreen(index++);
        const legendLabel = dataItem.deviceNames?.[key] || key; // Use device name or key as label
        datasets.push({
          label: legendLabel, // Use device name or key as label
          data: rawTimeValues.map((v) => v.value), // Extract values for the dataset
          borderColor: color,
          backgroundColor: color,
          fill: false,
          pointRadius: 0, // Hide dots for better readability
          pointHoverRadius: 0,
          // Disable bezier interpolation so each segment is straight
          tension: 0,
          spanGaps: false,
          borderWidth: 2, // Consistent line width
          type: 'line',
        });
      });
    });
    // Process all labels to ensure they match
    const labels = this.processLabels(allLabels);
    this.updateChart(labels, datasets);
  }

  private updateChart(labels: any[], datasets: any[]) {
    super.updateChartType(this.interval);
    this.chartOptions.set(this.monthLineChartOptions);
    this.chartData.set({
      labels: labels,
      datasets: datasets,
    });
    if (this.chart) {
      this.chart.update();
    }
  }

  protected getGreen(index: number): string {
    const root = document.documentElement;
    const bluePalette = [
      getComputedStyle(root).getPropertyValue('--color-battery-green').trim(), // primary blue from CSS variable
      '#A8E6A1', // light green
      '#71B451', // standard green
      '#4A9234', // deep green
      '#9CD96B', // fresh/sky green
      '#1F5D25', // dark green
    ];
    return bluePalette[index % bluePalette.length];
  }
}
